import React from "react";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import {
  CurbsuiteTabScopeFilter,
  DateRangeFilter,
  GatewayTransactionType,
  GET_CURBSUITE_TRANSACTIONS,
  GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
  GetCurbsuiteTransactionsFilter,
  GetCurbsuiteTransactionsVars,
  TransactionOrderBy,
} from "common/graphql/queries/GetCurbsuiteTransactions";
import { CurbPassTransactionsTableVariant } from "../types";
import { PAGE_SIZE } from "../useTransactionsUrlState";
import { ADJUSTMENT_TYPE_LABEL } from "../../common/adjustments";
import { WALLET_TRANSACTION_TYPE_LABELS } from "../columns/walletTransactionTypeLabels";
import { dateOperatorFilter, licensePlateFilter, operatorNameFilter, transactionIndexFilter } from "./filterHelpers";
import { TransactionsQueryResult, useTransactionsQuery } from "./useTransactionsQuery";

/**
 * The always-on part of the Transactions / Monthly Charges filter: which rows back the tab.
 *
 * Each tab shows one transaction type plus (AUT-6163) the adjustments -- chargebacks and credits --
 * whose referenced charge belongs on that tab, so a reversal sits next to the charge it reverses:
 *
 * | Tab                              | Rows                       | Adjustments referencing   |
 * | -------------------------------- | -------------------------- | ------------------------- |
 * | park (standard)                  | park                       | park                      |
 * | park (airport, "Transactions")   | trip_charge                | (none can exist)          |
 * | wallet (standard)                | gateway_transaction        | wallet_reload or permit   |
 * | wallet (airport, Monthly Charges)| permit gateway_transaction | permit                    |
 *
 * (Bus-station park is served by GET_BUS_STATION_TRANSACTIONS, whose own WHERE adds adjustments
 * referencing park or permit.)
 *
 * GSP's Monthly Charges tab must show ONLY the permit reconciliation charges the month-end charging
 * script writes. Its other gateway rows -- `initial_wallet_load` and `wallet_refund` -- carry no
 * reconciled period, so `getReconciledMonth` returns null for them and the Month column falls back
 * to the month of their settlement date. That renders them indistinguishably from a real monthly
 * charge (a refund as a negative one) and inflates or deflates the month's total.
 *
 * Scoped to airports because every other wallet payee's tab is *about* those rows.
 */
export function buildCurbsuiteBaseFilter({
  variant,
  parkTransactionType,
  isAirportWallet,
}: {
  variant: CurbPassTransactionsTableVariant;
  parkTransactionType: "park" | "trip_charge";
  isAirportWallet: boolean;
}): CurbsuiteTabScopeFilter {
  if (variant === "park") {
    // Only park rows can be adjusted (GMS rejects trip_charge references), so the airport
    // Transactions tab gets no adjustment arm.
    return {
      _or: [
        { transaction_type: { _eq: parkTransactionType } },
        ...(parkTransactionType === "park"
          ? [{ transaction_type: { _eq: "adjustment" }, reference_transaction_type: { _eq: "park" } }]
          : []),
      ],
    };
  }
  if (isAirportWallet) {
    return {
      _or: [
        {
          transaction_type: { _eq: "gateway_transaction" },
          transaction: { gateway_transactions: { gateway_transaction_type: { _eq: "permit" } } },
        },
        { transaction_type: { _eq: "adjustment" }, reference_gateway_transaction_type: { _eq: "permit" } },
      ],
    };
  }
  return {
    _or: [
      { transaction_type: { _eq: "gateway_transaction" } },
      {
        transaction_type: { _eq: "adjustment" },
        reference_gateway_transaction_type: { _in: ["wallet_reload", "permit"] },
      },
    ],
  };
}

/**
 * The full `$filter` for GET_CURBSUITE_TRANSACTIONS: the tab scope (buildCurbsuiteBaseFilter) ANDed
 * with the user's column filters.
 */
export function buildCurbsuiteTransactionsFilter({
  variant,
  parkTransactionType,
  isAirportWallet,
  appliedFilters,
}: {
  variant: CurbPassTransactionsTableVariant;
  parkTransactionType: "park" | "trip_charge";
  isAirportWallet: boolean;
  appliedFilters: GridFilterModel | undefined;
}): GetCurbsuiteTransactionsFilter {
  const baseFilter = buildCurbsuiteBaseFilter({ variant, parkTransactionType, isAirportWallet });

  if (!appliedFilters) {
    return { _and: [baseFilter] };
  }

  const columnFilters = appliedFilters.items
    .map((item) => {
      if (item.columnField === "transactionIndex") {
        return transactionIndexFilter(item.value);
      }
      if (item.columnField === "transactionType") {
        if (item.value === ADJUSTMENT_TYPE_LABEL) {
          return { transaction_type: { _eq: "adjustment" } };
        }
        // Match the display label first, but fall back to the raw enum so filters serialized
        // before this change (e.g. a bookmarked "...:equals:wallet_reload" URL) still work
        // instead of silently dropping the filter and showing every row.
        const gatewayTransactionType = (Object.keys(WALLET_TRANSACTION_TYPE_LABELS) as GatewayTransactionType[]).find(
          (type) => WALLET_TRANSACTION_TYPE_LABELS[type] === item.value || type === item.value,
        );
        if (!gatewayTransactionType) {
          return undefined;
        }
        return {
          transaction: {
            gateway_transactions: {
              gateway_transaction_type: { _eq: gatewayTransactionType },
            },
          },
        };
      }
      // transactionAssignedDate is a bus-station-park-only column (this query path never renders
      // it), but a filter on it can still arrive here: the filter model is rehydrated from the
      // URL, not derived from the current column set, and switching tabs away from bus-station
      // park clears page/sort but leaves filter/filterOperator in place (AUT-5621). Handling it
      // keeps that stale filter restrictive instead of being silently dropped, which would fold
      // an "and" filter to `_and: []` and render the full unfiltered result set while the filter
      // chip still reads as active.
      if (item.columnField === "transactionDate" || item.columnField === "transactionAssignedDate") {
        const graphQLField =
          item.columnField === "transactionDate" ? "transaction_date" : "assigned_to_account_at_local_date";
        return dateOperatorFilter(graphQLField, item.operatorValue, item.value);
      }
      if (item.columnField === "licensePlate") {
        return licensePlateFilter(item.operatorValue, item.value);
      }
      // Airports render the Operator column on this query path (bus stations use
      // GET_BUS_STATION_TRANSACTIONS, which handles it separately). Without this the filter would
      // be silently dropped and the grid would show unfiltered rows.
      if (item.columnField === "operator" && item.value != null) {
        return operatorNameFilter(item.value);
      }
      return undefined;
    })
    .filter(Boolean) as Record<string, unknown>[];

  // Separate conjuncts, so an "or" column filter can't overwrite the tab scope's own `_or`.
  return {
    _and: [baseFilter, appliedFilters.linkOperator === "and" ? { _and: columnFilters } : { _or: columnFilters }],
  };
}

/**
 * GET_CURBSUITE_TRANSACTIONS wiring: filter builder plus the query/export-batch plumbing shared with
 * useBusStationTransactionsData via useTransactionsQuery. Serves every shape except bus-station-park,
 * which reads from useBusStationTransactionsData instead (a different query entirely, since bus
 * stations mix "park" and "gateway_transaction" transaction types in one tab).
 */
export function useCurbsuiteTransactionsData({
  variant,
  parkTransactionType,
  isAirportWallet,
  appliedFilters,
  dateRangeFilter,
  orderBy,
  page,
  skip,
}: {
  variant: CurbPassTransactionsTableVariant;
  parkTransactionType: "park" | "trip_charge";
  isAirportWallet: boolean;
  appliedFilters: GridFilterModel | undefined;
  dateRangeFilter: DateRangeFilter;
  orderBy: TransactionOrderBy[];
  page: number;
  skip: boolean;
}): TransactionsQueryResult {
  const filter: GetCurbsuiteTransactionsFilter = React.useMemo(
    () => buildCurbsuiteTransactionsFilter({ variant, parkTransactionType, isAirportWallet, appliedFilters }),
    [variant, appliedFilters, parkTransactionType, isAirportWallet],
  );

  const exportVariables = React.useMemo(
    () => ({
      orderBy,
      filter,
      dateRangeFilter,
      priceFilter: GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
    }),
    [orderBy, filter, dateRangeFilter],
  );

  const variables: GetCurbsuiteTransactionsVars = React.useMemo(
    () => ({ ...exportVariables, offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }),
    [exportVariables, page],
  );

  return useTransactionsQuery<GetCurbsuiteTransactionsVars>(
    GET_CURBSUITE_TRANSACTIONS,
    variables,
    exportVariables,
    skip,
  );
}
